<script setup lang="ts">
import {
  apartmentTypes,
  prices2026,
  dlAparthotel2026Aug,
  type ApartmentTypeId,
} from '~/utils/pricing2026';

const { t } = useI18n();

function savingPercent(ours: number, dl: number): number {
  return Math.round(((ours - dl) / dl) * 100);
}

const rows = apartmentTypes.map((type: ApartmentTypeId) => {
  const ours = prices2026[type].aug;
  const dl = dlAparthotel2026Aug[type];
  return { type, ours, dl, savePct: savingPercent(ours, dl) };
});
</script>

<template>
  <section class="relative overflow-hidden pt-6 md:pt-8 pb-4 md:pb-8 bg-white dark:bg-slate-900">
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -left-16 h-80 w-80 rounded-full bg-amber-200/25 blur-3xl dark:bg-amber-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-amber-200/25 blur-3xl dark:bg-amber-500/10"
    />
    <div data-reveal class="relative container">
      <div class="text-center max-w-2xl mx-auto mb-10">
        <div class="flex items-center justify-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
          <span>{{ t('prices.dl.title') }}</span>
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
        </div>
        <p class="mt-4 font-body text-slate-500 dark:text-slate-400">
          {{ t('prices.dl.subtitle') }}
        </p>
      </div>

      <!-- Desktop / tablet: table layout -->
      <div class="mt-10 hidden sm:block overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <table class="w-full border-collapse text-sm font-body">
          <thead>
            <tr class="border-b border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/40">
              <th scope="col" class="text-left px-6 py-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500 dark:text-slate-400">
                {{ t('prices.dl.col.type') }}
              </th>
              <th scope="col" class="text-center px-4 py-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-amber-700 dark:text-amber-300">
                {{ t('prices.dl.col.us') }}
              </th>
              <th scope="col" class="text-center px-4 py-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500 dark:text-slate-400">
                {{ t('prices.dl.col.dl') }}
              </th>
              <th scope="col" class="text-center px-4 py-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-red-500 dark:text-red-400">
                {{ t('prices.dl.col.save') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.type"
              class="border-t border-slate-100 dark:border-slate-800"
            >
              <th scope="row" class="text-left px-6 py-6 font-body font-semibold text-sm text-slate-900 dark:text-white">
                {{ t(`prices.table.type.${row.type}`) }}
              </th>
              <td class="text-center px-4 py-6 tabular-nums font-display italic text-2xl text-amber-700 dark:text-amber-300">
                ${{ row.ours }}
              </td>
              <td class="text-center px-4 py-6 tabular-nums font-body text-base text-slate-500 dark:text-slate-400 line-through decoration-slate-400/40">
                ${{ row.dl }}
              </td>
              <td class="text-center px-4 py-6">
                <span class="inline-flex items-center gap-1 rounded-full bg-red-500/10 dark:bg-red-500/15 px-3 py-1 font-body font-semibold text-sm text-red-600 dark:text-red-400 tabular-nums">
                  <Icon name="lucide:arrow-down" class="size-3.5" aria-hidden="true" />
                  {{ Math.abs(row.savePct) }}%
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile: stacked cards -->
      <ul class="mt-10 sm:hidden space-y-4">
        <li
          v-for="row in rows"
          :key="row.type"
          class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5"
        >
          <p class="font-body font-semibold text-base text-slate-900 dark:text-white">
            {{ t(`prices.table.type.${row.type}`) }}
          </p>
          <div class="mt-4 flex items-end justify-between gap-4">
            <div>
              <p class="text-[10px] uppercase tracking-[0.2em] text-amber-700 dark:text-amber-300 font-body font-semibold">
                {{ t('prices.dl.col.us') }}
              </p>
              <p class="mt-1 font-display italic text-2xl tabular-nums text-amber-700 dark:text-amber-300">
                ${{ row.ours }}
              </p>
              <p class="mt-1 text-xs font-body text-slate-500 dark:text-slate-400 line-through">
                ${{ row.dl }} · {{ t('prices.dl.col.dl') }}
              </p>
            </div>
            <span class="inline-flex items-center gap-1 rounded-full bg-red-500/10 dark:bg-red-500/15 px-3 py-1.5 font-body font-semibold text-sm text-red-600 dark:text-red-400 tabular-nums">
              <Icon name="lucide:arrow-down" class="size-3.5" aria-hidden="true" />
              {{ Math.abs(row.savePct) }}%
            </span>
          </div>
        </li>
      </ul>

    </div>
  </section>
</template>
